import { useEffect, useMemo, useState } from 'react';
import { Icon, IconButton, LinearProgress, TablePagination, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Button, Typography, AlertColor } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ComunicacaoService } from 'shared/services/comunicacao/comunicacaoService';
import { IListagemComunicacao, IListagemComunicacaoLog } from 'shared/types';
import { useSelectedOrganization } from 'shared/store/organization';
import { FerramentasDaListagem, IListaFiltros, AlertaSnackBar } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { useHasPermissionAcao } from 'shared/hooks';
import { NotificationsActiveOutlined, SmsOutlined, EmailOutlined } from '@mui/icons-material';
import { IKeyValue, listagemCanalToIKeyValue, stringToDateTime } from 'shared/utils';
import { CanaisService } from 'shared/services/canais/canaisService';

export const ListagemDeLogsComunicacao: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const hasPermitionCreate = useHasPermissionAcao('comunicacao', 'CRE');
  const hasPermitionRead = useHasPermissionAcao('comunicacao', 'REA');

  const org = useSelectedOrganization();

  const [stateSnack, setStateSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const [rows, setRows] = useState<IListagemComunicacaoLog[]>([]);
  const [listKeyValueCanal, setListKeyValueCanal] = useState<IKeyValue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCanal, setIsLoadingCanal] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [eventoPesquisar, setEventoPesquisar] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const buscaId = useMemo(() => {
    return searchParams.get('com_id') || [];
  }, [searchParams]);

  const buscaNome = useMemo(() => {
    return searchParams.get('com_nm') || '';
  }, [searchParams]);

  const buscaCanal = useMemo(() => {
    return searchParams.get('canal_id') || [];
  }, [searchParams]);

  const offSet = useMemo(() => {
    return searchParams.get('offset') || '0';
  }, [searchParams]);

  const limit = useMemo(() => {
    return searchParams.get('limit') || '10';
  }, [searchParams]);



  const listaFiltros: IListaFiltros[] = [
    {
      tipo: 'TextFieldNumeric',
      mapValue: [],
      label: t('pages.comunicacao.idcomunicacao'),
      textoBusca: buscaId,
      handleTextoBusca: texto => { searchParams.set('com_id', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'TextField',
      mapValue: [],
      label: t('pages.comunicacao.titulo'),
      textoBusca: buscaNome,
      handleTextoBusca: texto => { searchParams.set('com_nm', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'Select',
      mapValue: listKeyValueCanal.map((itemk) => itemk),
      label: t('pages.orgFornecedora.Canal'),
      textoBusca: buscaCanal,
      handleTextoBusca: texto => { searchParams.set('canal_id', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
  ];

  const handleCloseSnack = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setStateSnack({ ...stateSnack, open: false });
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    searchParams.set('offset', (newPage * rowsPerPage).toString()); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    searchParams.set('limit', event.target.value.toString());
    searchParams.set('offset', '0');
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleLimpar = () => {
    // eslint-disable-next-line prefer-const
    let parametros: string[] = [];
    searchParams.forEach(function (value, key) {
      parametros.push(key);
    });
    parametros.map((item) => (
      searchParams.delete(item)
    ));
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handlePesquisar = () => {
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  useEffect(() => {
    setIsLoadingCanal(true);

    CanaisService.getAll()
      .then((result) => {
        setIsLoadingCanal(false);
        if (result instanceof Error) {
          setStateSnack({open:true, severity:'error',message:`Ocorreu o seguinte erro: ${result.message}`});
        } else {
          setListKeyValueCanal(listagemCanalToIKeyValue(result));
        }
      });
  },[]);

  useEffect(() => {

    setIsLoading(true);
    ComunicacaoService.getAllLogs(searchParams.toString())
      .then((result) => {
        setIsLoading(false);

        if (result instanceof Error) {
          setStateSnack({ open: true, severity: 'error', message: `Ocorreu o seguinte erro: ${result.message}` });
        } else {
          setTotalCount(result.count);
          setRows(result.rows);

          setRowsPerPage(Number.parseInt(limit.toString()));
          setPage(Number.parseInt(offSet.toString()) / Number.parseInt(limit.toString()));
        }
      });
  }, [eventoPesquisar]);

  return (
    <>
      <LayoutBaseDePagina
        titulo={t('pages.comunicacao.titulolog')}

        drawerDeFiltros={
          <FerramentasDaListagem
            listaFiltros={listaFiltros}
            handlePesquisar={() => handlePesquisar()}
            handleLimpar={() => handleLimpar()}
          />
        }
      >

        <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
          <Table stickyHeader >
            <TableHead>
              <TableRow>
                <TableCell>{t('pages.comunicacao.titulo')}</TableCell>
                <TableCell>{t('pages.comunicacao.canal')}</TableCell>
                <TableCell>{t('pages.comunicacao.fornecedora')}</TableCell>
                <TableCell>{t('pages.comunicacao.descricao')}</TableCell>
                <TableCell>{t('global.acoes')}</TableCell>
                <TableCell> {t('global.dataHora')}</TableCell>
              </TableRow>
            </TableHead>
            {!isLoading && (<TableBody>
              {rows.map((row) => (
                <TableRow key={row.IdComunicacao}>
                  <TableCell>{row.MGCONFCOMLOG_NM}</TableCell>
                  <TableCell>{row.Canal}</TableCell>
                  <TableCell>{row.Fornecedora}</TableCell>
                  <TableCell>{row.MGCONFCOMLOG_DS}</TableCell>
                  <TableCell>{row.Acao}</TableCell>
                  <TableCell>{stringToDateTime(row.DATA_ATUALIZACAO)}</TableCell>
                </TableRow>
              ))}
            </TableBody>)}

            {totalCount === 0 && !isLoading && (
              <caption>{t('global.listaVazia')}</caption>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <LinearProgress variant='indeterminate' />
                  </TableCell>
                </TableRow>
              )}
              {(!isLoading) && (
                <TableRow>
                  <TableCell colSpan={2}>
                    {hasPermitionCreate && (<Button
                      color='primary'
                      disableElevation
                      variant='outlined'
                      onClick={() => navigate('/organizacao/listagem')}
                      startIcon={<Icon>arrow_back</Icon>}
                    >
                      <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                        {t('components.actions.voltar')}
                      </Typography>
                    </Button>
                    )}

                  </TableCell>
                  <TableCell colSpan={8}>
                    <TablePagination
                      component="div"
                      labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('global.of')} ${count !== -1 ? count : `${t('global.moreThan')} ${to}`}`}
                      labelRowsPerPage={t('global.rowsPerPage')}
                      showFirstButton={true}
                      showLastButton={true}
                      page={page}
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[5, 8, 10, 20, 50, 100]}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    //onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>
      </LayoutBaseDePagina>
      <AlertaSnackBar
        open={stateSnack.open}
        severity={stateSnack.severity as AlertColor}
        message={stateSnack.message}
        handleClose={handleCloseSnack}
      />
    </>
  );
};